import moment from 'moment'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { matchPath, useHistory, useLocation } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { loadPersistedReduxState } from '@/components/App/actions'
import Page from '@/components/Page'
import { changeCurrentSpace } from '@/store/actions'
import { getConfiguration, getCurrentUser } from '@/store/selectors'
import AuthenticationProvider from '@/utilities/AuthenticationProvider'
import clientLocalStorage from '@/utilities/clientLocalStorage'
import ServiceURL from '@/utilities/ServiceURL'

import { authenticationFailure, authenticationSignout, authenticationSuccess } from './actions'
import messages from './messages'
import { getSessionExpirationMoment, isAuthenticationError } from './selectors'

const REDIRECT_STORAGE_KEY = 'redirectUrl'

const isExpiringSoon = (expirationMoment: any) => {
  return expirationMoment && moment().isSameOrAfter(expirationMoment.clone().subtract(2, 'minutes'))
}

function AuthenticationHandler(props: any) {
  const history = useHistory()
  const location = useLocation<{
    updatedSpaceRoles: boolean
    from?: { pathname: string; search?: string }
  }>()

  const { authorizeUrl, clientId, cognito } = props.configuration
  const isDevEnv = process.env.NODE_ENV !== 'production'
  const redirectUrl = isDevEnv
    ? `${window.location.origin}${process.env.PUBLIC_URL}`
    : ServiceURL(props.configuration).console

  const auth = new AuthenticationProvider(
    {
      AppWebDomain: new URL(authorizeUrl).host,
      ClientId: clientId,
      RedirectUriSignIn: `${redirectUrl}/signin`,
      RedirectUriSignOut: `${redirectUrl}/signout`,
      TokenScopesArray: ['profile', 'openid', 'com.workgrid.api/console.all', 'com.workgrid.api/userclient.all']
    },
    // for local environments, we provide a mock Cognito access token for the `onSuccess` callback
    isDevEnv && process.env.REACT_APP_USE_DEV_ENV !== 'true'
      ? { email: process.env.REACT_APP_MOCK_USER, iss: `${window.location.origin}/${cognito.userPoolId}` }
      : undefined
  )

  auth.userhandler = { onFailure: onCognitoFailure, onSuccess: onCognitoSuccess }

  useEffect(() => {
    function componentDidMount() {
      if (isLogInComplete()) {
        auth.parseCognitoWebResponse(location.hash)
      } else if (isLogOutInProgress()) {
        if (location.state && location.state.updatedSpaceRoles) {
          clientLocalStorage.setItem('updatedSpaceRoles', true)
        }
        auth.signOut()
      } else if (isLogOutComplete()) {
        const updatedSpaceRoles = clientLocalStorage.getItem('updatedSpaceRoles')
        clientLocalStorage.removeItem('updatedSpaceRoles')
        props.actions.authenticationSignout()
        updatedSpaceRoles ? history.push('/loggedout', { updatedSpaceRoles }) : history.push('/loggedout')
      } else if (isLogInInProgress()) {
        if (location.state && location.state.from) {
          clientLocalStorage.setItem(
            REDIRECT_STORAGE_KEY,
            location.state.from.pathname + (location.state.from.search || '')
          )
        }

        /*
         * Cognito will return a cached session when possible so we need to clear Cognito's cached session or the user
         * will repeatedly be prompted with a session expiration (warning) message.
         */
        if (isExpiringSoon(props.sessionExpiration)) {
          auth.setUsername(null as unknown as string)
          auth.clearCachedTokensScopes()
          // @ts-expect-error
          auth.signInUserSession = auth.getCachedSession()
        }

        auth.getSession()
      }
    }
    componentDidMount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function onCognitoFailure() {
    props.actions.authenticationFailure()
  }

  async function onCognitoSuccess(session: any) {
    if (!session.isValid()) {
      return props.actions.authenticationFailure()
    }

    // persisted redux state tree must be restored first or the authentication session will be overwritten
    await props.actions.loadPersistedReduxState()
    await props.actions.authenticationSuccess(session)

    const redirectUrl = clientLocalStorage.getItem(REDIRECT_STORAGE_KEY)

    if (redirectUrl) {
      const match = matchPath<{ spaceId?: string }>(redirectUrl, { path: '/space/:spaceId' })

      if (match !== null && match.params.spaceId) {
        clientLocalStorage.setItem('selectedSpace', match.params.spaceId)
        await props.actions.changeCurrentSpace(match.params.spaceId)
      }

      history.push(redirectUrl)
      clientLocalStorage.removeItem(REDIRECT_STORAGE_KEY)
    } else {
      history.push('/')
    }
  }

  const getStatusMessage = () => {
    if (isLogInInProgress()) {
      return <FormattedMessage {...messages.loggingIn} />
    } else if (isLogOutInProgress()) {
      return <FormattedMessage {...messages.loggingOut} />
    } else if (isLogOutComplete()) {
      return <FormattedMessage {...messages.loggedOut} />
    } else if (isLogInComplete()) {
      return <FormattedMessage {...messages.loggedIn} />
    }

    return null
  }

  const isLogInComplete = () => !!location.hash && location.pathname === '/signin'

  const isLogOutComplete = () => location.pathname === '/signout'

  const isLogInInProgress = () => location.pathname === '/login'

  const isLogOutInProgress = () => location.pathname === '/logout'

  if (props.isError) {
    return (
      <p>
        <FormattedMessage {...messages.error} />
      </p>
    )
  }

  const message = getStatusMessage()

  return (
    <Page isLoading={true} loadingMessage={message}>
      <p>{message}</p>
    </Page>
  )
}

AuthenticationHandler.displayName = 'AuthenticationHandler'

AuthenticationHandler.propTypes = {
  actions: PropTypes.shape({
    authenticationFailure: PropTypes.func.isRequired,
    authenticationSignout: PropTypes.func.isRequired,
    authenticationSuccess: PropTypes.func.isRequired,
    changeCurrentSpace: PropTypes.func.isRequired,
    loadPersistedReduxState: PropTypes.func.isRequired
  }).isRequired,
  configuration: PropTypes.shape({
    apiHost: PropTypes.string.isRequired,
    clientId: PropTypes.string.isRequired,
    authorizeUrl: PropTypes.string.isRequired
  }).isRequired,
  currentUser: PropTypes.object,
  isError: PropTypes.bool,
  sessionExpiration: PropTypes.object
}

AuthenticationHandler.defaultProps = {
  currentUser: null,
  isError: false,
  sessionExpiration: null
}

export { /** @deprecated */ AuthenticationHandler }

const mapStateToProps = (state: any) => ({
  configuration: getConfiguration(state),
  currentUser: getCurrentUser(state),
  isError: isAuthenticationError(state),
  sessionExpiration: getSessionExpirationMoment(state)
})

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      authenticationFailure,
      authenticationSignout,
      authenticationSuccess,
      changeCurrentSpace,
      loadPersistedReduxState
    },
    dispatch
  )
})

export default connect(mapStateToProps, mapDispatchToProps)(AuthenticationHandler)
